/**
 *
 *  SetEmail
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useState } from "react";
import { useAppState } from "../../../state";
import { useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";

// Icons
import CheckNiceIcon from "../../../icons/CheckNiceIcon";

export default function SetEmail({ ctaText, onNext }) {
  const { error, isFetching } = useAppState();

  const { authVendor, userEmail, setUserEmail, hasAuthError } =
    useSignUpState();

  const [canSend, setCanSend] = useState(false);
  const [email, setEmail] = useState(userEmail);
  const [hasError, setHasError] = useState(false);
  const [hasEmailError, setHasEmailError] = useState(false);

  const emailRef = React.useRef();

  useEffect(() => {
    if (userEmail && isValidEmail(userEmail)) {
      setCanSend(true);
    }
  }, [userEmail]);

  const handleEmailChange = useCallback((e) => {
    const value = e.target.value;

    setEmail(value);
    setHasError(false);

    if (isValidEmail(value)) {
      setCanSend(true);
      setHasEmailError(false);
    } else {
      setCanSend(false);
    }
  }, []);

  const handleEmailSend = useCallback((evt) => {
    evt && evt.preventDefault(); // We might call this callback directly, it's not a good practice thou

    let email = emailRef.current.value;

    if (!isValidEmail(email)) {
      setHasEmailError(true);

      return;
    }

    if (email) {
      setUserEmail(email);

      onNext({ email });
    }
  }, []);

  useEffect(() => {
    if (authVendor !== "email" && emailRef.current && emailRef.current.value) {
      handleEmailSend();
    }

    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  return (
    <div className="tbk-relative tbk-rounded-lg tbk-bg-basic-white">
      <div className="tbk-text-title-bold tbk-mb-1 tbk-text-blue-dark">
        What is Your Email?
      </div>
      {authVendor !== "email" ? (
        <p className="tbk-text-main tbk-mb-4 tbk-text-blue-dark">
          Email is required to get bookings confirmations, receipts, feedback
          and notifications.
        </p>
      ) : null}
      <form onSubmit={handleEmailSend}>
        <div className="tbk-mt-4 tbk-mb-3">
          <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
            Your email:
          </div>
          <input
            ref={emailRef}
            type="email"
            placeholder="Your email"
            className={`tbk-input-field tbk-text-main tbk-mb-auto tbk-mr-1 tbk-pt-[11px] tbk-pl-2 tbk-pr-2 tbk-pb-[11px]${
              hasError || hasEmailError ? " tbk-border-coral" : ""
            }`}
            value={email}
            onChange={handleEmailChange}
          />
        </div>
        {hasError || hasEmailError || hasAuthError ? (
          <div className="tbk-text-main tbk--mt-2 tbk-mb-2 tbk-text-center tbk-text-coral">
            {error ? error.message : "Please, check your email and try again."}
          </div>
        ) : null}
        <Button
          type="submit"
          className="tbk-mb-3 tbk-w-full"
          //variant="secondary"
          disabled={!canSend || isFetching}
          endIcon={<CheckNiceIcon />}
          showLoader={isFetching}
          // onClick={}
        >
          {ctaText || "Finish registration"}
        </Button>
      </form>
    </div>
  );
}

function isValidEmail(email) {
  return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
